// Derives the Academy Portal's navy-chrome + accent theme from an academy's
// brand colour (a plain hex string, academy.branding.primaryColor). The
// portal's CSS (src/styles/academyPortal.css) ships a default emerald theme
// as static var values on .ap-shell — this only needs to produce an override
// object when an academy actually has a brand colour; the caller spreads it
// onto the shell root's inline style, and any var it doesn't set falls back
// to the stylesheet default via normal CSS cascade.
//
// Conversion is real sRGB -> OKLab -> OKLCH (Björn Ottosson's formulas), not
// a lookup table, so it works for any hex an academy picks. The specific L/C
// offsets per token below are reverse-engineered from the redesign's four
// hand-authored worked examples (emerald/sapphire/amber/crimson in the
// prototype's ap-tokens.css) rather than exact numbers from anywhere — those
// four swatches don't follow one clean formula themselves (they're hand
// picked), so this is a defensible approximation of their intent, not a
// pixel match.

function hexToLinearRgb(hex) {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  const int = parseInt(full, 16);
  const toLinear = (c8) => {
    const c = c8 / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return {
    r: toLinear((int >> 16) & 255),
    g: toLinear((int >> 8) & 255),
    b: toLinear(int & 255),
  };
}

function linearRgbToOklch({ r, g, b }) {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const b2 = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
  const C = Math.sqrt(a * a + b2 * b2);
  let H = (Math.atan2(b2, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { L, C, H };
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const oklch = (L, C, H) => `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`;

// A low-chroma navy still reads as "dark neutral chrome" at almost any hue
// (that's the whole point of keeping C at 0.02-0.04), except in the
// red/red-orange band, where even a faint tint skews the navy brown/muddy
// rather than cool-dark. The crimson worked example (brand H≈20) shifts its
// navy hue all the way to 280 for exactly this reason, while amber
// (H≈62, outside the band) and sapphire (H≈245, already cool) barely move.
function coolNavyHue(brandH) {
  const inWarmBand = brandH <= 50 || brandH >= 340;
  return inWarmBand ? 270 : brandH;
}

// Returns null (use the stylesheet's default emerald theme) when there's no
// brand colour to derive from.
export function deriveAcademyTheme(primaryColorHex) {
  if (!primaryColorHex || typeof primaryColorHex !== 'string') return null;
  const hex = primaryColorHex.trim();
  if (!/^#?[0-9a-fA-F]{3}$|^#?[0-9a-fA-F]{6}$/.test(hex)) return null;

  const { L: brandL, H: brandH } = linearRgbToOklch(hexToLinearRgb(hex));
  const accentL = clamp(brandL, 0.50, 0.64);
  const navyH = coolNavyHue(brandH);

  return {
    '--accent': oklch(accentL, 0.16, brandH),
    '--accent-deep': oklch(Math.max(0.30, accentL - 0.11), 0.14, brandH),
    '--accent-light': oklch(0.95, 0.05, brandH),
    '--accent-ink': oklch(0.40, 0.13, brandH),
    '--navy': oklch(0.17, 0.035, navyH),
    '--navy-2': oklch(0.225, 0.035, navyH),
    '--navy-3': oklch(0.30, 0.032, navyH),
  };
}
